/**
 * Spec-line builder (S3, §4.2 + A3 + CONSULTS §4) — the card's one line of
 * scent. Pure function of the recipe's clamped values.
 *
 * Rules:
 * - VARYING FIELDS ONLY (A3 whitelist): word target, tone, section count
 *   (within clamps), list/callout density, link counts. Baseline components
 *   (FAQ, table, images, TOC, key takeaways) NEVER appear — a differentiator
 *   must differentiate; they live in the Shape and the detail pane.
 * - Ranges display floor-clamped (A1): a 3–7 config with a ≥5-section floor
 *   reads 5–7, never a range the pipeline can't emit.
 * - The superscript floor tick is the SOLE in-line marking (CONSULTS §4) —
 *  `floored` flags here feed exactly that tick; color may second, never
 *   carry. No bold, no underline, no prefix glyphs.
 * - Fitness-first: no negative framing ("no table/no TOC" is banned copy).
 */

import { GenerationRecipe } from '../recipeTypes';
import { ShapeFloorPayload } from './shapeFloor';
import { clampRecipeToFloor } from './clampToFloor';
import { GALLERY_STRINGS } from '../galleryStrings';

export interface SpecSegment {
  key: 'words' | 'sections' | 'tone' | 'list' | 'callouts' | 'links';
  /** Display text, e.g. "1,800 WORDS" or "5–7 SECTIONS". */
  text: string;
  /** True when the production floor raised this value — the sole input to
   *  the raised superscript tick. */
  floored: boolean;
  /** Footnote for the tick tooltip (present iff floored). */
  floorNote?: string;
}

const formatCount = (n: number): string => n.toLocaleString('en-US');

/**
 * Build the spec-line segments for a recipe. Ordering is stable:
 * words → sections → tone → list → callouts → links.
 */
export function buildSpecLine(
  recipe: GenerationRecipe,
  floor: ShapeFloorPayload | undefined,
): SpecSegment[] {
  const clamped = clampRecipeToFloor(recipe, floor);
  const segments: SpecSegment[] = [];

  // 1 — Word target (floor-clamped).
  segments.push({
    key: 'words',
    text: `${formatCount(clamped.words.value)} ${GALLERY_STRINGS.specLine.wordsUnit}`,
    floored: clamped.words.floored,
    floorNote: clamped.words.floored
      ? `${GALLERY_STRINGS.specLine.floorTickNote} (≥ ${formatCount(floor?.rules.min_words ?? 0)} words)`
      : undefined,
  });

  // 2 — Section count (floor-clamped range; never a range the pipeline
  // can't emit).
  const min = clamped.minSections.value;
  const max = clamped.maxSections.value;
  const sectionText =
    min === max
      ? `${min} ${GALLERY_STRINGS.specLine.sectionsUnit}`
      : `${min}–${max} ${GALLERY_STRINGS.specLine.sectionsUnit}`;
  segments.push({
    key: 'sections',
    text: sectionText,
    floored: clamped.minSections.floored,
    floorNote: clamped.minSections.floored
      ? `${GALLERY_STRINGS.specLine.floorTickNote} (≥ ${floor?.body_section_floor ?? 0} sections)`
      : undefined,
  });

  // 3 — Tone.
  segments.push({ key: 'tone', text: recipe.tone.toUpperCase(), floored: false });

  // 4 — List density (only when the recipe/floor actually produces lists).
  if (clamped.glyphs.list && recipe.maxListItems) {
    segments.push({
      key: 'list',
      text: `LIST ≤${recipe.maxListItems}`,
      floored: false,
    });
  }

  // 5 — Callout density (floor-clamped).
  if (clamped.glyphs.callout && clamped.callouts.value > 0) {
    const n = clamped.callouts.value;
    segments.push({
      key: 'callouts',
      text: `${n} ${n === 1 ? 'CALLOUT' : 'CALLOUTS'}`,
      floored: clamped.callouts.floored,
      floorNote: clamped.callouts.floored
        ? `${GALLERY_STRINGS.specLine.floorTickNote} (≥ ${floor?.rules.min_callouts ?? 0})`
        : undefined,
    });
  }

  // 6 — Link counts (only explicit counts; null/undefined = AI decides and
  // carries no scent).
  const linkParts: string[] = [];
  if (recipe.internalLinkCount != null) linkParts.push(`${recipe.internalLinkCount} INT`);
  if (recipe.externalLinkCount != null) linkParts.push(`${recipe.externalLinkCount} EXT`);
  if (linkParts.length > 0) {
    segments.push({ key: 'links', text: linkParts.join(' · '), floored: false });
  }

  return segments;
}
